import { DeliveryMethodLayout, HighlightContentPreviewId } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { stringify } from '@/helpers/string';
import useConfirmationNavigate from '@/hooks/useConfirmationNavigate';
import { highlightPreviewSelector } from '@/redux/slice/highlightPreview.slice';
import previewSlice from '@/redux/slice/preview.slice';
import ruleSlice from '@/redux/slice/rule.slice';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { IRule } from '@/types/newSetup.type';
import { Link } from '@shopify/polaris';
import { memo, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import DeliveryMethodRadio from '../DeliveryMethodRadio';
import DeliveryMethodSelect from '../DeliveryMethodSelect';
import RegularText from '../RegularText';
import { sortRulesBy } from '@/helpers';

interface IProps {
  rules?: IRule[];
  country?: string;
  flag?: string;
  zipcode?: string;
  isEmptyRule?: boolean;
}

const PreviewMethod = (props: IProps) => {
  const location = useLocation();
  const navigate = useConfirmationNavigate();
  const dispatch = useDispatch();
  const dataSettings = useSelector(dataSettingsSelector);
  const [selected, setSelected] = useState(0);
  const { isHighlightedShippingMethodHeader } = useSelector(highlightPreviewSelector);

  const sortedRules = useMemo(() => {
    return sortRulesBy(props.rules || [], dataSettings.ruleSortBy);
  }, [props.rules, dataSettings.ruleSortBy]);

  useEffect(() => {
    setSelected((sortedRules || [])[0]?.id || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSettings.choose_layout, stringify(sortedRules)]);

  useEffect(() => {
    if (sortedRules) {
      dispatch(previewSlice.actions.handleUpdateSelectedRule(sortedRules?.find((item) => item.id === selected)));
    }
  }, [dispatch, selected, sortedRules]);

  if (!sortedRules) return null;

  return (
    <>
      {dataSettings.enable_eta_information ? (
        sortedRules.length > 0 ? (
          dataSettings.choose_layout === DeliveryMethodLayout.Card ? (
            <div className="ot-estimated-shipping-shipping-methods">
              <div className="ot-estimated-shipping-shipping-methods-select-box">
                {dataSettings.show_shipping_method ? (
                  <label
                    htmlFor={HighlightContentPreviewId.ShippingMethodHeader}
                    className={`mb-8 d-inline-block ${isHighlightedShippingMethodHeader ? 'item-highlight-outline' : ''}`}
                  >
                    <h4 style={{ color: dataSettings.text_color, fontSize: dataSettings.text_size }}>
                      {dataSettings.shippingMethodLabel}
                    </h4>
                  </label>
                ) : null}
                {sortedRules.map((item, _, arr) => {
                  return (
                    <DeliveryMethodRadio
                      rule={item}
                      showRadioInput={arr.length > 1}
                      key={item.id || 0}
                      id={item.id || 0}
                      name={item.styledName || item.name}
                      checked={selected === item.id}
                      onChange={() => setSelected(item.id || 0)}
                      estText={item.estimatedText || ''}
                      orderToShipDays={Number(item.minPrepDays)}
                      preDays={Number(item.maxPrepDays)}
                      shipToReceiveDays={Number(item.minDeliveryDays)}
                      estDays={Number(item.maxDeliveryDays)}
                      isShowDeliveryMethodName={!!item.isShowDeliveryMethodName}
                      country={props.country}
                      flag={props.flag}
                      zipcode={props.zipcode}
                      privacyPageUrl={item.privacyPageUrl || ''}
                      privacyPageText={item.privacyPageText || ''}
                      privacyMore={item.privacyMore || ''}
                      isUsePreOrderDate={item.isUsePreOrderDate || false}
                      preOrderDate={item.preOrderDate || ''}
                    />
                  );
                })}
              </div>
            </div>
          ) : (
            <DeliveryMethodSelect
              rules={sortedRules}
              selected={selected}
              setSelected={setSelected}
              country={props.country}
              flag={props.flag}
              zipcode={props.zipcode}
            />
          )
        ) : props.isEmptyRule ? (
          <RegularText>
            You must create at least one{' '}
            <Link
              onClick={() => {
                navigate(PATH.CREATE_RULE, {
                  state: {
                    previousPath: {
                      pathname: location.pathname,
                      search: location.search,
                    },
                  },
                });
                dispatch(ruleSlice.actions.handleInit());
              }}
              removeUnderline
            >
              <strong>Rule</strong>
            </Link>{' '}
            to see the preview
          </RegularText>
        ) : null
      ) : null}
    </>
  );
};

export default memo(PreviewMethod);
